/**
 * UserList Component
 * Displays and manages all system users (superadmin only)
 */

import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { USER_ROLES, QUERY_KEYS } from "../../utils/constants";
import { UserRole } from "../../types/auth.types";
import type { Profile } from "../../types/auth.types";
import LoadingSpinner from "../common/LoadingSpinner";
import Select from "../common/Select";
import { formatDate } from "../../utils/formatters";
import UserDetailModal from "./UserDetailModal";
import ResetPasswordModal from "./ResetPasswordModal";
import { passwordResetGuard } from "../../utils/passwordResetEligibility";
import Badge from "../ui/Badge";

interface UserWithStatus extends Profile {
  status?: "Active" | "Inactive" | "Pending";
  emailConfirmed?: boolean;
}

const UserList: React.FC = () => {
  const { user: currentUser, permissions } = useAuth();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState<Profile | null>(null);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"name" | "last_login" | "created_at">(
    "created_at",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Fetch all users with status
  const {
    data: users,
    isLoading,
    error,
  } = useQuery<UserWithStatus[]>({
    queryKey: [QUERY_KEYS.USERS],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      // Filter out soft-deleted users (email starts with "deleted_")
      const activeProfiles = (profiles || []).filter(
        (profile) => !profile.email?.startsWith("deleted_"),
      );

      // Determine status based on last_login and is_active
      // Pending = never logged in (last_login is null)
      // Active = logged in before AND is_active = true
      // Inactive = logged in before AND is_active = false
      const usersWithStatus: UserWithStatus[] = activeProfiles.map(
        (profile) => {
          let status: "Active" | "Inactive" | "Pending" = "Pending";
          const hasLoggedIn = profile.last_login !== null;

          if (hasLoggedIn && profile.is_active) {
            status = "Active";
          } else if (hasLoggedIn && !profile.is_active) {
            status = "Inactive";
          }

          return {
            ...profile,
            status,
            emailConfirmed: hasLoggedIn, // If they've logged in, email was confirmed
          };
        },
      );

      return usersWithStatus;
    },
    enabled: !!permissions?.can_manage_users,
  });

  // Determine if current user is super_admin
  const isSuperAdmin =
    currentUser?.role === UserRole.SUPER_ADMIN ||
    currentUser?.role === "superadmin";

  // Filter and sort users
  const filteredAndSortedUsers = users
    ?.filter((user) => {
      // Admin users cannot see super_admin profiles
      if (
        !isSuperAdmin &&
        (user.role === UserRole.SUPER_ADMIN ||
          user.role === ("superadmin" as UserRole))
      ) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          user.full_name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.role?.toLowerCase().includes(query);

        if (!matchesSearch) {
          return false;
        }
      }

      // Role filter
      if (roleFilter !== "all" && user.role !== roleFilter) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "name": {
          const nameA = a.full_name || a.email || "";
          const nameB = b.full_name || b.email || "";
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case "last_login": {
          const dateA = a.last_login ? new Date(a.last_login).getTime() : 0;
          const dateB = b.last_login ? new Date(b.last_login).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
        case "created_at": {
          const createdA = new Date(a.created_at).getTime();
          const createdB = new Date(b.created_at).getTime();
          comparison = createdA - createdB;
          break;
        }
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

  // Handle sorting
  const handleSort = (column: "name" | "last_login" | "created_at") => {
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  // Get sort icon for column
  const getSortIcon = (column: "name" | "last_login" | "created_at") => {
    if (sortBy !== column) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortOrder === "asc" ? (
      <svg
        className="w-4 h-4 ml-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  // Handle user click to open detail modal
  const handleUserClick = (user: Profile) => {
    // Prevent non-super_admins from editing super_admin users
    if (
      !isSuperAdmin &&
      (user.role === UserRole.SUPER_ADMIN ||
        user.role === ("superadmin" as UserRole))
    ) {
      return;
    }
    setSelectedUser(user);
    setIsDetailModalOpen(true);
  };

  // Handle user updated. Also invalidate the support-agents queries
  // because the profile-mirror trigger (migration 2026-06-04-004)
  // flips support_agents.is_active server-side when a profile is
  // deactivated or its role moves out of the support family. The
  // Manage Agents modal needs to see the change without a reload.
  const handleUserUpdated = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
    queryClient.invalidateQueries({ queryKey: ["agents-with-supervisors"] });
    queryClient.invalidateQueries({
      queryKey: ["agents-with-supervisors-all"],
    });
  };

  // Handle user deleted — same invalidation cascade as above.
  const handleUserDeleted = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
    queryClient.invalidateQueries({ queryKey: ["agents-with-supervisors"] });
    queryClient.invalidateQueries({
      queryKey: ["agents-with-supervisors-all"],
    });
  };

  // Get role label
  const getRoleLabel = (role: UserRole): string => {
    const roleOption = USER_ROLES.find((r) => r.value === role);
    return roleOption?.label || role;
  };

  // Whether the current admin/superadmin can reset THIS user's password (pure logic in
  // passwordResetGuard; the manage-user edge function is the authz source of truth).
  const callerRole = (currentUser?.role || "").toLowerCase();
  const callerCanReset = callerRole === "superadmin" || callerRole === "admin";
  const resetGuard = (u: UserWithStatus) =>
    passwordResetGuard(callerRole, currentUser?.id, u);

  // Get status badge variant
  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case "Active":
        return "success";
      case "Inactive":
        return "default";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  // Only show to users with can_manage_users permission
  if (!permissions?.can_manage_users) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">
          You do not have permission to view this page.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading users..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error loading users. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search Bar */}
          <div>
            <label
              htmlFor="user-search"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Search Users
            </label>
            <input
              id="user-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or role..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Role Filter */}
          <div>
            <label
              htmlFor="role-filter"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Filter by Role
            </label>
            <Select
              id="role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              fullWidth
            >
              <option value="all">All Roles</option>
              {USER_ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {(searchQuery || roleFilter !== "all") && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredAndSortedUsers?.length || 0} of {users?.length || 0}{" "}
            users
          </p>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredAndSortedUsers && filteredAndSortedUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center">
                      Name
                      {getSortIcon("name")}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("last_login")}
                  >
                    <div className="flex items-center">
                      Last Login
                      {getSortIcon("last_login")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("created_at")}
                  >
                    <div className="flex items-center">
                      Date Created
                      {getSortIcon("created_at")}
                    </div>
                  </th>
                  {callerCanReset && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleUserClick(user)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.avatar_url ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user.avatar_url}
                              alt={user.full_name}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium text-sm">
                                {user.full_name
                                  ? user.full_name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .toUpperCase()
                                      .slice(0, 2)
                                  : user.email[0].toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                            {user.full_name || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getStatusBadgeVariant(user.status)}>
                        {user.status || "Unknown"}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_login ? formatDate(user.last_login) : "Never"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    {callerCanReset &&
                      (() => {
                        const g = resetGuard(user);
                        if (!g.show) {
                          return (
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-300">
                              —
                            </td>
                          );
                        }
                        return (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <button
                              type="button"
                              title={g.disabledReason}
                              disabled={!!g.disabledReason}
                              onClick={(e) => {
                                e.stopPropagation();
                                setResetUser(user);
                                setIsResetOpen(true);
                              }}
                              className="font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                            >
                              Reset password
                            </button>
                          </td>
                        );
                      })()}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      <UserDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
        onUserDeleted={handleUserDeleted}
      />

      {/* Admin password-reset modal */}
      <ResetPasswordModal
        isOpen={isResetOpen}
        onClose={() => {
          setIsResetOpen(false);
          setResetUser(null);
        }}
        user={resetUser}
      />
    </div>
  );
};

export default UserList;
